<template>
  <Container>
    <template v-slot:header>System Status</template>

    <template v-slot:content>
      <div class="w-full max-w-2xl">
        <div class="bg-white rounded-xl shadow-md p-8">
          <div v-if="hasRecord" class="text-left mb-5">
            <span class="inline-flex flex-wrap">
              <span
                v-if="savedBannerActive"
                class="px-2 text-green-800 bg-green-100 rounded-full border-gray-200"
                >Live</span
              >
              <span
                v-else
                class="px-2 text-gray-800 bg-gray-100 rounded-full border-gray-200"
                >Draft &mdash; not shown to users</span
              >
            </span>
          </div>

          <form @submit.prevent="onSubmit" class="flex flex-col gap-5">
            <div>
              <label class="text-sm font-medium text-left text-gray-700"
                >Status</label
              >
              <select v-model="status" class="form-field-no-icon w-full mt-1">
                <option
                  v-for="opt in statusOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
            </div>

            <div>
              <label class="text-sm text-left font-medium text-gray-700">
                Banner Active
              </label>
              <select
                v-model="bannerActive"
                class="form-field-no-icon w-full mt-1"
              >
                <option :value="true">True</option>
                <option :value="false">False</option>
              </select>
            </div>

            <div>
              <label class="text-sm text-left font-medium text-gray-700">
                Banner Message
              </label>
              <textarea
                v-model="bannerMessage"
                :maxlength="maxMessageLength"
                rows="3"
                class="form-field-no-icon w-full mt-1 outline-none resize-none"
              />
              <p class="text-xs text-right text-gray-500">
                {{ bannerMessage.length }}/{{ maxMessageLength }}
              </p>
            </div>

            <div>
              <label class="text-sm text-left font-medium text-gray-700">
                Banner Severity
              </label>
              <select
                v-model="bannerSeverity"
                class="form-field-no-icon w-full mt-1"
              >
                <option
                  v-for="opt in severityOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
            </div>

            <div>
              <label class="text-sm text-left font-medium text-gray-700">
                Degraded Features
              </label>
              <input
                type="text"
                v-model="degradedFeaturesInput"
                class="form-field-no-icon w-full mt-1 outline-none"
              />
            </div>

            <div class="pt-3 flex gap-3">
              <button
                type="submit"
                :disabled="!isValid"
                class="button-submit w-full"
              >
                Confirm
              </button>

              <button
                type="button"
                :disabled="!hasRecord"
                @click="onDelete"
                class="button-cancel w-full"
              >
                Deactivate
              </button>
            </div>
          </form>
        </div>
      </div>
    </template>
  </Container>
</template>

<script>
import Container from "@/components/common/Container";
import { ref, computed } from "vue";
import formMixin from "@/mixins/formMixin";
import useFormErrors from "@/api/formErrors";
import useConfirmationModal from "@/api/confirmationModal";
import eventBus from "@/utils/eventBus";

// Keep in sync with SystemStatus.BANNER_MESSAGE_MAX_LENGTH on the backend.
const MAX_MESSAGE_LENGTH = 280;

export default {
  name: "SystemStatusAdminForm",
  mixins: [formMixin],
  components: {
    Container,
  },
  setup() {
    const { errors, formActions } = useFormErrors();

    const currentId = ref(null);
    const status = ref("operational");
    const bannerActive = ref(false);
    const bannerMessage = ref("");
    const bannerSeverity = ref("info");
    const degradedFeaturesInput = ref("");

    // Whether a record exists at all, and whether the saved record has the banner
    // enabled. These are distinct: a record can exist as a draft with the banner off.
    const hasRecord = ref(false);
    const savedBannerActive = ref(false);

    const statusOptions = ref([
      { value: "operational", label: "Operational" },
      { value: "degraded", label: "Degraded" },
      { value: "maintenance", label: "Maintenance" },
      { value: "down", label: "Down" },
    ]);

    const severityOptions = ref([
      { value: "info", label: "Info" },
      { value: "warning", label: "Warning" },
      { value: "error", label: "Error" },
    ]);

    const isValid = computed(() => {
      if (!status.value) return false;
      if (!bannerSeverity.value) return false;
      // A message is only required to turn the banner on; the status can be
      // changed on its own while the banner stays off.
      if (bannerActive.value && !bannerMessage.value.trim()) return false;
      return true;
    });

    return {
      currentId,
      status,
      bannerActive,
      bannerMessage,
      bannerSeverity,
      degradedFeaturesInput,
      statusOptions,
      severityOptions,
      errors,
      formActions,
      isValid,
      hasRecord,
      savedBannerActive,
      maxMessageLength: MAX_MESSAGE_LENGTH,
    };
  },

  mounted() {
    this.fetchStatus();
  },

  methods: {
    async fetchStatus() {
      try {
        const data = await this.$alfredService.getSystemStatusAdmin();

        if (!data || Object.keys(data).length === 0) {
          this.hasRecord = false;
          this.savedBannerActive = false;
          return;
        }

        this.hasRecord = true;
        this.currentId = data.id || null;
        this.status = data.status || "operational";
        this.bannerActive = data.banner_active ?? false;
        this.savedBannerActive = this.bannerActive;
        this.bannerMessage = data.banner_message || "";
        this.bannerSeverity = data.banner_severity || "info";
        this.degradedFeaturesInput = (data.degraded_features || []).join(", ");
      } catch (err) {
        console.error("Fetch error:", err);
      }
    },

    async onSubmit() {
      if (!this.isValid) return;

      const modal = useConfirmationModal();

      await modal.openModal({
        title: "Confirm Action",
        message: `Are you sure you want to ${
          this.hasRecord ? "update" : "create"
        } system status?`,
        onSubmit: () => this.handleSubmit(),
      });
    },

    async handleSubmit() {
      const payload = {
        status: this.status,
        banner_active: this.bannerActive,
        banner_message: this.bannerMessage,
        banner_severity: this.bannerSeverity,
        degraded_features: this.degradedFeaturesInput
          ? this.degradedFeaturesInput
              .split(",")
              .map((f) => f.trim())
              .filter(Boolean)
          : [],
      };

      const apiCall = this.hasRecord
        ? () => this.$alfredService.updateSystemStatus(this.currentId, payload)
        : () => this.$alfredService.createSystemStatus(payload);

      const wasExisting = this.hasRecord;

      const response = await this.handleSubmissionErrors(apiCall, {
        formActions: this.formActions,
        throwExceptions: false,
      });

      if (response) {
        eventBus.trigger("notify", {
          notification: {
            group: "top-right",
            type: "success",
            text: wasExisting
              ? "System status updated successfully"
              : "System status created successfully",
          },
        });

        await this.fetchStatus();
      }
    },

    async onDelete() {
      const modal = useConfirmationModal();

      await modal.openModal({
        title: "Delete System Status",
        message:
          "Are you sure you want to deactivate the active system status?",
        onSubmit: async () => {
          const response = await this.handleSubmissionErrors(
            () => this.$alfredService.deleteSystemStatus(this.currentId),
            { formActions: this.formActions, throwExceptions: false }
          );

          if (response) {
            eventBus.trigger("notify", {
              notification: {
                group: "top-right",
                type: "success",
                text: "System status deactivated successfully",
              },
            });
            this.hasRecord = false;
            this.savedBannerActive = false;
            this.currentId = null;
            this.status = "operational";
            this.bannerActive = false;
            this.bannerMessage = "";
            this.bannerSeverity = "info";
            this.degradedFeaturesInput = "";
          }
        },
      });
    },
  },
};
</script>
